import * as React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilePicker,
  Input,
  Label,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../../lib/requestErrorToast';
import { useBusyDialogGuard } from '../../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../../lib/messages';
import { readShigoAppConfig } from '../../../../../../lib/config';
import { ShigoNetwork } from '../../../../../../lib/deployment';
import { useDomainComplianceMutations } from '../../../hooks';
import {
  createPublisherDomainFormValue,
  serializePublisherDomainCreate,
  serializePublisherDomainUpdate,
  publisherDomainToFormValue,
  validatePublisherDomainForm,
  type PublisherDomainFormValue,
} from '../../../publisherDomainForm';
import { PublisherDomainsAddMethod, type PublisherDomainListItem } from '../../../model';

export function PublisherDomainDialog({
  publisherId,
  method,
  onClose,
  domain,
}: {
  publisherId: string;
  method: PublisherDomainsAddMethod;
  onClose: () => void;
  domain?: PublisherDomainListItem;
}) {
  const [value, setValue] = React.useState(() =>
    domain ? publisherDomainToFormValue(domain) : createPublisherDomainFormValue(),
  );
  const [file, setFile] = React.useState<File | null>(null);
  const [submitted, setSubmitted] = React.useState(false);
  const mutations = useDomainComplianceMutations();
  // Explicitly approved Kiwi-only legacy exception.
  const showTriton = readShigoAppConfig().network === ShigoNetwork.Kiwi;
  const pending =
    mutations.createPublisherDomain.isPending ||
    mutations.importPublisherDomains.isPending ||
    mutations.updatePublisherDomain.isPending;
  const guard = useBusyDialogGuard(pending, onClose);
  const errors = validatePublisherDomainForm(value, showTriton);
  const valid =
    method === PublisherDomainsAddMethod.Single ? Object.keys(errors).length === 0 : Boolean(file);
  const visibleErrors = submitted ? errors : {};

  function patch(next: Partial<PublisherDomainFormValue>) {
    setValue((current) => ({ ...current, ...next }));
  }

  async function submit() {
    setSubmitted(true);
    if (!valid || pending) return;
    try {
      if (domain) {
        await mutations.updatePublisherDomain.mutateAsync({
          publisherId,
          domainId: domain.domainId,
          payload: serializePublisherDomainUpdate(domain, value, showTriton),
        });
      } else if (method === PublisherDomainsAddMethod.Single) {
        await mutations.createPublisherDomain.mutateAsync({
          publisherId,
          payload: serializePublisherDomainCreate(publisherId, value, showTriton),
        });
      } else if (file) {
        await mutations.importPublisherDomains.mutateAsync({ publisherId, file });
      }
      toast.success(
        dashboardT(
          domain ? 'publisherDetails.domains.editSuccess' : 'publisherDetails.domains.addSuccess',
        ),
      );
      onClose();
    } catch (error) {
      showRequestError(
        error,
        dashboardT(
          domain ? 'publisherDetails.domains.editError' : 'publisherDetails.domains.addError',
        ),
      );
    }
  }

  let title = dashboardT('publisherDetails.domains.addMultipleTitle');
  let description = dashboardT('publisherDetails.domains.addMultipleDescription');
  if (method === PublisherDomainsAddMethod.Single) {
    title = dashboardT('publisherDetails.domains.addSingleTitle');
    description = dashboardT('publisherDetails.domains.addSingleDescription');
  }
  if (domain) {
    title = dashboardT('publisherDetails.domains.edit');
    description = domain.url;
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent
        className="flex max-h-[92vh] max-w-3xl flex-col gap-0 overflow-hidden p-0"
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="break-all">{description}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {method === PublisherDomainsAddMethod.Single ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="publisher-domain-name">
                  {dashboardT('publisherDetails.domains.domain')} *
                </Label>
                <Input
                  id="publisher-domain-name"
                  aria-label={dashboardT('publisherDetails.domains.domain')}
                  placeholder="example.com"
                  value={value.domain}
                  disabled={pending}
                  aria-invalid={Boolean(visibleErrors.domain)}
                  onChange={(event) => {
                    patch({ domain: event.target.value });
                  }}
                />
                {visibleErrors.domain ? (
                  <p role="alert" className="text-destructive text-xs">
                    {dashboardT('publisherDetails.domains.invalidDomain')}
                  </p>
                ) : null}
              </div>
              {showTriton ? (
                <div className="space-y-2">
                  <Label htmlFor="publisher-domain-cpm">
                    {dashboardT('supplyDomains.triton.today')}
                  </Label>
                  <Input
                    id="publisher-domain-cpm"
                    type="number"
                    min="0"
                    step="any"
                    value={value.todayTritonCpm}
                    disabled={pending}
                    aria-invalid={Boolean(visibleErrors.todayTritonCpm)}
                    onChange={(event) => {
                      patch({ todayTritonCpm: event.target.value });
                    }}
                  />
                  {visibleErrors.todayTritonCpm ? (
                    <p role="alert" className="text-destructive text-xs">
                      {dashboardT('supplyDomains.triton.invalid')}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <FilePicker
              label={dashboardT('publisherDetails.csvFile')}
              buttonLabel={dashboardT('publisherDetails.chooseFile')}
              accept=".csv,text/csv"
              value={file}
              onFileChange={setFile}
              required
              disabled={pending}
            />
          )}
        </div>
        <DialogFooter className="border-border border-t px-5 py-4">
          <Button type="button" variant="outline" disabled={pending} onClick={guard.close}>
            {dashboardT('publisherDetails.cancel')}
          </Button>
          <Button
            type="button"
            disabled={pending || (method === PublisherDomainsAddMethod.Multiple && !file)}
            onClick={() => void submit()}
          >
            {dashboardT('publisherDetails.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
